<template>
  <div ref="tableContainer" class="employee-deductions-table">
    <DataTable
      ref="dataTableRef"
      data-testid="payroll-deductions-table"
      display-mode="handle_user_device"
      :config="tableConfig"
      :data="enrichedDeductions"
      :auto-load="false"
      :refreshButton="{ show: true }"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick"
      @refresh="$emit('refresh')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '../../../composables/useDateFormatter';
import { log } from '../../../utils/DebugLogger';

type Id = number | string;

interface Deduction {
  id: Id;
  status: number | string;
  amount: number | string;
  amount_paid?: number | string;
  amount_remaining?: number | string;
  created_at?: Date | string | null;
  employee_name?: string;
  title?: string;
  status_label?: string;
  status_severity?: string;
  [key: string]: unknown;
}

interface EnrichedDeduction extends Deduction {
  status_display: string;
  status_severity: string;
  created_at_parsed: Date | null;
  amount_display: number;
  amount_paid_display: number;
  amount_remaining_display: number;
}

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

interface ActionRow {
  status: number | string;
  [key: string]: unknown;
}

interface Props {
  deductions?: Record<string, unknown>[];
  isLoading?: boolean;
  showUser?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  deductions: () => [],
  isLoading: false,
  showUser: true,
  canEdit: true,
  canDelete: true
});

const emit = defineEmits<{
  'edit': [deduction: EnrichedDeduction | { id: Id }];
  'view': [deduction: EnrichedDeduction | { id: Id }];
  'cancel': [deduction: EnrichedDeduction | { id: Id }];
  'reopen': [deduction: EnrichedDeduction | { id: Id }];
  'delete': [deduction: EnrichedDeduction | { id: Id }];
  'refresh': [];
}>();

const { t, n } = useI18n();
const { formatDateTime: formatDateTimeLocale } = useDateFormatter();

// Refs
const dataTableRef = ref<unknown>(null);
const tableContainer = ref<HTMLElement | null>(null);

// Computed: Enriched deductions data
const enrichedDeductions = computed<EnrichedDeduction[]>(() => {
  return (props.deductions as Deduction[]).map(deduction => {
    // Status display - deduction has its own status_label from composable
    const statusDisplay = {
      label: (deduction.status_label as string) || t('Desconhecido'),
      severity: (deduction.status_severity as string) || 'secondary'
    };

    // Parse dates
    const createdAt = deduction.created_at instanceof Date
      ? deduction.created_at
      : deduction.created_at ? new Date(deduction.created_at as string) : null;

    return {
      ...deduction,
      status_display: statusDisplay.label,
      status_severity: statusDisplay.severity,
      created_at_parsed: createdAt,
      amount_display: parseFloat(String(deduction.amount)) || 0,
      amount_paid_display: parseFloat(String(deduction.amount_paid ?? 0)) || 0,
      amount_remaining_display: parseFloat(String(deduction.amount_remaining ?? 0)) || 0
    };
  });
});

// Render functions
const renderStatus = ({ value, data }: CellParams): string => {
  const row = data as { status_severity?: string };
  const severity = row.status_severity || 'secondary';
  return `<span class="p-tag p-tag-${severity}">${value}</span>`;
};

const renderAmount = ({ data }: CellParams): string => {
  const row = data as { amount_display?: number };
  const amount = row.amount_display || 0;
  return `<span class="amount-value">${n(amount, 'currency')}</span>`;
};

const renderAmountPaid = ({ data }: CellParams): string => {
  const row = data as { amount_paid_display?: number; amount_display?: number };
  const amountPaid = row.amount_paid_display || 0;
  const amount = row.amount_display || 0;
  const percentage = amount > 0 ? (amountPaid / amount * 100).toFixed(0) : 0;
  return `
    <div class="amount-paid-container">
      <span class="amount-value">${n(amountPaid, 'currency')}</span>
      <span class="amount-percentage">${percentage}%</span>
    </div>
  `;
};

const renderAmountRemaining = ({ data }: CellParams): string => {
  const row = data as { amount_remaining_display?: number };
  const amountRemaining = row.amount_remaining_display || 0;
  return `<span class="amount-value amount-remaining">${n(amountRemaining, 'currency')}</span>`;
};

const renderCreatedAt = ({ data }: CellParams): string => {
  const row = data as { created_at_parsed?: Date | null };
  const date = row.created_at_parsed;
  if (!date || isNaN(date.getTime())) {
    return `<span class="text-muted">-</span>`;
  }
  return formatDateTimeLocale(date);
};

// Status filter options
const statusFilterOptions = computed(() => [
  { label: t('Aberto'), value: 1 },
  { label: t('Parcial'), value: 2 },
  { label: t('Pago'), value: 3 },
  { label: t('Cancelado'), value: 4 }
]);

// Table configuration
const tableConfig = computed(() => {
  const columns: Record<string, unknown>[] = [
    {
      data: 'id',
      title: t('ID'),
      renderType: 'clickable_id',
      minWidth: 70,
      sortable: true
    },
    {
      data: 'title',
      title: t('Título'),
      minWidth: 180,
      sortable: true
    },
    {
      data: 'amount_display',
      title: t('Valor'),
      minWidth: 100,
      sortable: true,
      cellRenderer: renderAmount
    },
    {
      data: 'amount_paid_display',
      title: t('Pago'),
      minWidth: 120,
      sortable: true,
      cellRenderer: renderAmountPaid
    },
    {
      data: 'amount_remaining_display',
      title: t('Restante'),
      minWidth: 120,
      sortable: true,
      cellRenderer: renderAmountRemaining
    },
    {
      data: 'status_display',
      title: t('Status'),
      minWidth: 100,
      sortable: true,
      filterType: 'select',
      filterOptions: statusFilterOptions.value,
      cellRenderer: renderStatus
    }
  ];

  // Add user column if showUser is true (employee_name comes from backend via Cognito)
  if (props.showUser) {
    columns.splice(5, 0, {
      data: 'employee_name',
      title: t('Colaborador'),
      minWidth: 150,
      sortable: true,
      render: (data: string | null | undefined): string => data || '-'
    });
  }

  // Add created_at column
  columns.push({
    data: 'created_at_parsed',
    title: t('Criado em'),
    minWidth: 140,
    sortable: true,
    cellRenderer: renderCreatedAt
  });

  // Build actions array for the actions column
  const actions: Array<{
    type: string;
    label: string;
    icon: string;
    severity?: string;
    showCondition?: (row: ActionRow) => boolean;
  }> = [];

  // Cancel button for open, partial, paid
  if (props.canDelete) {
    actions.push({
      type: 'cancel',
      label: t('Cancelar'),
      icon: 'pi pi-times',
      severity: 'danger',
      showCondition: (row: ActionRow) => [1, 2, 3].includes(parseInt(String(row.status)))
    });
  }

  // Reopen button for canceled
  actions.push({
    type: 'reopen',
    label: t('Reabrir'),
    icon: 'pi pi-refresh',
    severity: 'warning',
    showCondition: (row: ActionRow) => parseInt(String(row.status)) === 4
  });

  // Delete button for open only
  if (props.canDelete) {
    actions.push({
      type: 'delete',
      label: t('Excluir'),
      icon: 'pi pi-trash',
      severity: 'danger',
      showCondition: (row: ActionRow) => parseInt(String(row.status)) === 1
    });
  }

  // View button for all
  actions.push({
    type: 'view',
    label: t('Ver'),
    icon: 'pi pi-eye',
    showCondition: (): boolean => true
  });

  // Add actions column if there are any actions
  if (actions.length > 0) {
    columns.push({
      data: null,
      title: t('Ações'),
      minWidth: 150,
      sortable: false,
      renderType: 'action_buttons',
      buttons: actions.map(action => ({
        text: action.label,
        icon: action.icon,
        action: action.type,
        class: action.severity ? `p-button-${action.severity}` : '',
        condition: action.showCondition
      }))
    });
  }

  return {
    columns,
    actions,
    emptyMessage: t('Nenhuma dedução encontrada'),
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [5, 10, 25, 50],
    rowTestId: (data: Record<string, unknown>): string => {
      const id = data?.id;
      return id == null ? '' : `payroll-deductions-row-${id}`;
    }
  };
});

// Event handlers
const handleEditClick = (value: Id | EnrichedDeduction): void => {
  log('EmployeeDeductionsTable', 'handleEditClick', 'Edit clicked', value);

  let deduction: EnrichedDeduction | null = null;

  if (typeof value === 'number' || typeof value === 'string') {
    deduction = enrichedDeductions.value.find(r => r.id === parseInt(String(value)) || r.id === value) ?? null;
    log('EmployeeDeductionsTable', 'handleEditClick', 'Found deduction by ID', { id: value, deduction });
  } else if (value && typeof value === 'object') {
    deduction = value as EnrichedDeduction;
  }

  if (deduction) {
    emit('edit', deduction);
  } else {
    log('EmployeeDeductionsTable', 'handleEditClick', 'Deduction not found, emitting ID only', value);
    emit('edit', { id: value as Id });
  }
};

interface ButtonClickPayload {
  action: string;
  data: EnrichedDeduction;
}

const handleButtonClick = ({ action, data }: ButtonClickPayload): void => {
  log('EmployeeDeductionsTable', 'handleButtonClick', 'Button clicked', { action, data });

  switch (action) {
    case 'edit':
      emit('edit', data);
      break;
    case 'view':
      emit('view', data);
      break;
    case 'cancel':
      emit('cancel', data);
      break;
    case 'reopen':
      emit('reopen', data);
      break;
    case 'delete':
      emit('delete', data);
      break;
  }
};

// Lifecycle
onMounted(() => {
  log('EmployeeDeductionsTable', 'onMounted', 'Component mounted');
});

onBeforeUnmount(() => {
  log('EmployeeDeductionsTable', 'onBeforeUnmount', 'Component unmounted');
});
</script>

<style scoped>
.employee-deductions-table {
  width: 100%;
}

:deep(.amount-value) {
  font-weight: 600;
  color: var(--primary-600);
}

:deep(.amount-remaining) {
  color: var(--orange-600);
}

:deep(.amount-paid-container) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
}

:deep(.amount-percentage) {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

:deep(.text-muted) {
  color: var(--text-color-secondary);
}
</style>
